// Section Zero Hero - Potential gauge + opportunity statement (map), value range (black band)
import { MapPin } from "lucide-react";
import { AnimatedGauge } from "@/components/hippo";
import { BAND_CONFIG } from "@/pages/hippo/constants";

const getEnhancementCategory = (strategies, fallbackGoal) => {
  if (!strategies || strategies.length === 0) return fallbackGoal || 'value enhancement';
  const primaryCategory = strategies[0]?.category || 'enhancement';
  const categoryNames = {
    'internal_reconfiguration': 'space optimisation',
    'additional_bedroom': 'bedroom enhancement',
    'secondary_space': 'secondary space',
    'bathroom': 'bathroom improvement',
    'cosmetic': 'presentation',
    'curb_appeal': 'curb appeal',
    'energy': 'energy efficiency'
  };
  return categoryNames[primaryCategory] || 'value enhancement';
};

const getBandColorClass = (band) => {
  if (band === 'Exceptional') return 'text-emerald-400';
  if (band === 'High') return 'text-cipher-green';
  if (band === 'Medium') return 'text-cipher-gold';
  if (band === 'Low') return 'text-amber-400';
  return 'text-gray-400'; // Negligible
};

const getBandBgClass = (band) => {
  if (band === 'Exceptional') return 'bg-emerald-500/15 border-emerald-500/30';
  if (band === 'High') return 'bg-cipher-green/15 border-cipher-green/30';
  if (band === 'Medium') return 'bg-cipher-gold/15 border-cipher-gold/30';
  if (band === 'Low') return 'bg-amber-500/15 border-amber-500/30';
  return 'bg-gray-500/15 border-gray-500/30'; // Negligible
};

const getPotentialStrength = (band) => {
  if (band === 'Exceptional') return 'exceptional';
  if (band === 'High') return 'strong';
  if (band === 'Medium') return 'real';
  if (band === 'Low') return 'some';
  return 'limited'; // Negligible
};

export function SectionZeroHero({
  hippo_band,
  overallScore,
  valueHeadroomRange,
  strategies,
  postcode,
  dwellingName,
  fallbackGoal,
  gaugeComplete,
  onGaugeComplete,
  percentile,
}) {
  const bandColorClass = getBandColorClass(hippo_band);
  const bandBgClass = getBandBgClass(hippo_band);
  const potentialStrength = getPotentialStrength(hippo_band);
  const enhancementCategory = getEnhancementCategory(strategies, fallbackGoal);
  const bandConfig = BAND_CONFIG[hippo_band] || BAND_CONFIG["Medium"];

  return (
    <div className="mb-5" data-testid="section-score-header">
      {/* Map + Gauge/Opportunity area */}
      <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-b from-gray-800/80 to-gray-900/80 border border-white/5 border-b-0">
        {/* Abstract map background */}
        <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="roadPattern" patternUnits="userSpaceOnUse" width="50" height="50">
              <path d="M0 25h50M25 0v50" stroke="#4B5563" strokeWidth="2" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#roadPattern)" />
          <path d="M0 100 Q100 80, 200 100 T400 100" stroke="#6B7280" strokeWidth="4" fill="none" />
          <path d="M200 0 Q180 100, 200 200" stroke="#6B7280" strokeWidth="4" fill="none" />
          <rect x="30" y="30" width="40" height="30" fill="#374151" rx="2" />
          <rect x="90" y="50" width="35" height="25" fill="#374151" rx="2" />
          <rect x="280" y="40" width="45" height="35" fill="#374151" rx="2" />
          <rect x="340" y="120" width="40" height="30" fill="#374151" rx="2" />
          <rect x="50" y="130" width="50" height="40" fill="#374151" rx="2" />
        </svg>

        {/* Gauge (left) | Divider | Postcode + Opportunity Statement (right) */}
        <div className="relative flex items-center gap-0 pl-6 pr-5 pt-10 pb-5" data-testid="score-value-reveal">
          {/* Potential gauge on the left with padding */}
          <div className={`flex-shrink-0 flex flex-col items-center transition-all duration-700 ease-out ${
            gaugeComplete ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="bg-black/40 backdrop-blur-sm rounded-full p-1.5">
              <AnimatedGauge value={overallScore} band={hippo_band} onAnimationComplete={onGaugeComplete} size="small" />
            </div>
            <p className="text-[10px] text-white/40 mt-2 uppercase tracking-wider">Potential</p>
          </div>

          {/* Center divider */}
          <div className={`h-28 w-px mx-5 bg-gradient-to-b from-transparent via-white/20 to-transparent transition-all duration-500 delay-300 flex-shrink-0 ${
            gaugeComplete ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'
          }`} />

          {/* Postcode + Band meaning (right, left-aligned, padded) */}
          <div className={`flex-1 flex flex-col justify-center pr-1 transition-all duration-700 ease-out delay-150 ${
            gaugeComplete ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            {/* Postcode directly above statement */}
            <div className="flex items-center gap-1.5 mb-2.5">
              <MapPin className="w-3 h-3 text-cipher-gold" />
              <span className="text-[10px] text-white/60 font-medium uppercase tracking-wider">{postcode || 'Your Area'}</span>
            </div>
            <p className="text-sm text-white/80 font-medium text-left leading-relaxed" data-testid="opportunity-statement">
              {bandConfig?.meaning || `Your ${dwellingName} shows ${potentialStrength} potential`}
            </p>
          </div>
        </div>
      </div>

      {/* Black band — Unlockable Value Range */}
      <div className="bg-black/80 px-4 py-3 border-t border-white/5" data-testid="value-band-strip">
        <div className={`flex items-center justify-center gap-3 transition-all duration-500 delay-400 ${gaugeComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <p className="text-[10px] text-white/40 uppercase tracking-wider">Unlockable Value Range</p>
          {valueHeadroomRange ? (
            <div className={`px-4 py-1.5 rounded-full border ${bandBgClass}`} data-testid="value-band">
              <p className={`text-base font-bold ${bandColorClass}`}>
                £{Math.round(valueHeadroomRange.min / 1000)}k–£{Math.round(valueHeadroomRange.max / 1000)}k
              </p>
            </div>
          ) : (
            <p className="text-sm text-white/30">Calculating...</p>
          )}
        </div>
      </div>

      {/* Band meaning + comparison bar + percentile */}
      <div className={`bg-white/[0.03] px-4 py-3 rounded-b-2xl border-t border-white/5 transition-all duration-500 delay-500 ${gaugeComplete ? 'opacity-100' : 'opacity-0'}`} data-testid="band-meaning">
        {bandConfig?.message && (
          <p className={`text-[11px] text-center leading-relaxed ${bandColorClass} opacity-80 mb-2.5`}>
            {bandConfig.message}
          </p>
        )}

        {/* Comparison bar — where your property sits */}
        {valueHeadroomRange && (
          <div className="mb-2">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[9px] text-white/25 w-10">Lower</span>
              <div className="flex-1 h-2.5 bg-white/5 rounded-full overflow-hidden relative">
                <div className="absolute inset-0 flex">
                  <div className="h-full bg-gray-500/30 rounded-l-full" style={{ width: '20%' }} />
                  <div className="h-full bg-amber-500/30" style={{ width: '20%' }} />
                  <div className="h-full bg-cipher-gold/30" style={{ width: '25%' }} />
                  <div className="h-full bg-cipher-green/30" style={{ width: '20%' }} />
                  <div className="h-full bg-emerald-400/30 rounded-r-full" style={{ width: '15%' }} />
                </div>
                {/* Your position marker */}
                <div
                  className="absolute top-0 h-full w-1 bg-white rounded-full shadow-lg shadow-white/30"
                  style={{ left: `${Math.min(95, Math.max(5, overallScore))}%` }}
                />
              </div>
              <span className="text-[9px] text-white/25 w-10 text-right">Higher</span>
            </div>
            <p className="text-[9px] text-white/30 text-center">Your property's position among similar homes</p>
          </div>
        )}

        {percentile !== undefined && (
          <p className="text-[10px] text-white/40 text-center" data-testid="percentile-inline">
            Higher than {overallScore}% of similar homes in {postcode?.split(" ")[0] || "your area"}
          </p>
        )}
        {valueHeadroomRange && (
          <p className="text-[9px] text-white/25 text-center mt-1" data-testid="value-explainer">
            Estimated value gain through targeted improvements — before costs
          </p>
        )}
      </div>
    </div>
  );
}
